import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import ReactDOM from 'react-dom'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useAppSettings } from '@/hooks/useAppSettings'
import { CHANGELOG, ChangelogEntry } from '@/data/changelog'
import { FONT } from '@/Components/Shell/tokens'
import { ChangelogLine } from '@/Components/ChangelogLine'

const SECTION_COLORS: Record<string, string> = {
  'New Features': '#22c55e',
  'Changes': '#f59e0b',
  'Fixes': '#3b82f6',
}

const FooterChangelogItems: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => {
  if (entry.sections && entry.sections.length > 0) {
    return (
      <>
        {entry.sections.map((section) => (
          <div key={section.label} style={{ marginBottom: '10px' }}>
            <p style={{
              fontSize: '10px',
              fontWeight: '700',
              color: SECTION_COLORS[section.label] || '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px',
            }}>
              {section.label}
            </p>
            <ul style={{ margin: 0, paddingLeft: '16px', listStyle: 'none' }}>
              {section.items.map((item, i) => (
                <li key={i} style={{
                  fontSize: '12px',
                  color: '#94a3b8',
                  lineHeight: '1.6',
                  position: 'relative',
                  paddingLeft: '8px',
                }}>
                  <span style={{ position: 'absolute', left: '-8px', color: '#64748b' }}>•</span>
                  <ChangelogLine text={item} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>
    )
  }
  return (
    <ul style={{ margin: 0, paddingLeft: '16px', listStyle: 'none' }}>
      {entry.changes.map((change, i) => (
        <li key={i} style={{
          fontSize: '12px',
          color: '#94a3b8',
          lineHeight: '1.6',
          position: 'relative',
          paddingLeft: '8px',
        }}>
          <span style={{ position: 'absolute', left: '-8px', color: '#64748b' }}>•</span>
          <ChangelogLine text={change} />
        </li>
      ))}
    </ul>
  )
}

// Derive from the changelog so the footer version can't drift behind a release.
const APP_VERSION = process.env.REACT_APP_VERSION || CHANGELOG[0].version.replace(/^v/, '')

/**
 * The version badge, its "What's New" popover, and the full release-notes modal.
 *
 * Lifted out of the Footer so the left nav can host it: the footer was a fixed
 * bar across every page for two links and this badge, and the nav already has a
 * bottom edge doing nothing.
 *
 * `align` decides which way the popover opens. In the footer it hangs off the
 * right edge; in the nav it opens from the left, and wider than the 196px rail
 * it is anchored to.
 */
export const VersionPill: React.FC<{ align?: 'left' | 'right' }> = ({ align = 'right' }) => {
  const [showChangelog, setShowChangelog] = useState(false)
  const [showAllNotes, setShowAllNotes] = useState(false)
  const badgeRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const latest = CHANGELOG[0]

  useEffect(() => {
    if (!showChangelog) return
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        badgeRef.current && !badgeRef.current.contains(e.target as Node)
      ) {
        setShowChangelog(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showChangelog])

  /**
   * ⚠️ The panel is PORTALLED and positioned from the badge's own box.
   *
   * It used to be an absolutely-positioned child of the badge, which worked while the
   * badge lived in a footer bar. The version pill now sits at the foot of the left nav,
   * and that nav is `overflow: hidden` (so its item list scrolls inside it rather than
   * pushing the badge below the fold), so the panel was clipped to the 196px rail and
   * the release notes were unreadable. No z-index fixes that: `overflow: hidden` on an
   * ancestor clips a descendant however high it stacks.
   */
  const [anchor, setAnchor] = useState<{ left: number; bottom: number } | null>(null)
  useLayoutEffect(() => {
    if (!showChangelog || !badgeRef.current) { setAnchor(null); return }
    const r = badgeRef.current.getBoundingClientRect()
    setAnchor({ left: r.left, bottom: window.innerHeight - r.top })
  }, [showChangelog])

  return (
    <div ref={badgeRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setShowChangelog(o => !o)}
        style={{
          background: 'none',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '3px 10px',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: '13px',
          color: '#94a3b8',
          fontWeight: '600',
          transition: 'border-color 0.15s, color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#475569'; e.currentTarget.style.color = '#cbd5e1' }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8' }}
      >
        v{APP_VERSION}
      </button>

      {showChangelog && anchor && ReactDOM.createPortal(
        <div
          ref={panelRef}
          style={{
            // ⚠️ The font has to be set HERE. The panel is portalled to document.body,
            // which is outside the `.font-pixel` subtree it used to inherit from when it
            // was a child of the badge, so portalling it silently dropped it to the
            // browser default.
            fontFamily: FONT,
            position: 'fixed',
            bottom: `${anchor.bottom + 8}px`,
            left: `${anchor.left}px`,
            width: isMobile ? '280px' : '340px',
            maxHeight: '60vh',
            backgroundColor: '#1e293b',
            border: '1px solid #334155',
            borderRadius: '10px',
            boxShadow: '0 -8px 30px rgba(0,0,0,0.4)',
            zIndex: 10060,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const,
          }}
        >
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid #334155',
            fontSize: '13px',
            fontWeight: '700',
            color: '#e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            What's New
            <span style={{ fontSize: '11px', fontWeight: '500', color: '#94a3b8' }}>{latest.date}</span>
          </div>
          <div style={{ padding: '12px 16px', overflowY: 'auto' as const, flex: 1, minHeight: 0 }}>
            <FooterChangelogItems entry={latest} />
          </div>
          {CHANGELOG.length > 1 && (
            <div style={{ padding: '8px 16px 12px', borderTop: '1px solid #334155', flexShrink: 0 }}>
              <button
                onClick={() => { setShowChangelog(false); setShowAllNotes(true) }}
                style={{
                  background: 'none', border: 'none', color: '#3b82f6',
                  fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0,
                }}
                onMouseEnter={e => (e.currentTarget.style.color = '#60a5fa')}
                onMouseLeave={e => (e.currentTarget.style.color = '#3b82f6')}
              >
                View all release notes
              </button>
            </div>
          )}
        </div>,
        document.body,
      )}

      {showAllNotes && ReactDOM.createPortal(
        <div
          onClick={() => setShowAllNotes(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            backgroundColor: 'rgba(0,0,0,0.6)',
            fontFamily: 'pressStart, monospace',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: isMobile ? '92vw' : '480px',
              maxHeight: '80vh',
              backgroundColor: '#1e293b',
              border: '1px solid #334155',
              borderRadius: '10px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column' as const,
            }}
          >
            <div style={{
              padding: '14px 18px', borderBottom: '1px solid #334155',
              fontSize: '14px', fontWeight: '700', color: '#e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
            }}>
              Release Notes
              <button
                onClick={() => setShowAllNotes(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '18px', cursor: 'pointer', padding: 0, lineHeight: 1 }}
              >×</button>
            </div>
            <div style={{ padding: '12px 18px', overflowY: 'auto' as const, flex: 1, minHeight: 0 }}>
              {CHANGELOG.map(entry => (
                <div key={entry.version} style={{ marginBottom: '18px' }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    marginBottom: '6px',
                  }}>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#e2e8f0' }}>{entry.version}</span>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>{entry.date}</span>
                  </div>
                  <FooterChangelogItems entry={entry} />
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

/**
 * The old fixed footer bar. MOBILE ONLY now — the desktop shell drops it and
 * puts the version pill at the foot of the left nav instead.
 */
export const Footer: React.FC = () => {
  const isMobile = useIsMobile()
  const { feedback_url, feedback_visible } = useAppSettings()

  return (
    <footer style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 40,
      backgroundColor: '#0f172a',
      borderTop: '1px solid #334155',
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: isMobile ? '10px 16px' : '10px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <a
            href="https://discord.gg/b4DZn3mVfP"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '13px', fontWeight: '500' }}
          >Discord</a>
          {feedback_visible && (
            <a
              href={feedback_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '13px', fontWeight: '500' }}
            >Feedback</a>
          )}
        </div>
        <VersionPill align="right" />
      </div>
    </footer>
  )
}
